/**
 * Which way a camera looks, read off the name a phone or tablet gives it:
 * "Front Camera" and "Back Camera" on an iPhone or iPad, "camera2 1, facing
 * front" on Android. A computer's webcam says neither, so a computer never
 * gets the pair.
 */
import type { DeviceChoice } from "./meeting";

export type Facing = "front" | "back";

export function facingOf(label: string): Facing | null {
  if (/\b(front|user|selfie)\b/i.test(label)) return "front";
  if (/\b(back|rear|environment)\b/i.test(label)) return "back";
  return null;
}

export interface FrontAndBack {
  front: DeviceChoice;
  back: DeviceChoice;
}

/**
 * The front camera and the main back one, when the device has both. An iPhone
 * lists several back lenses ("Back Dual Wide Camera", "Back Ultra Wide
 * Camera"); the plain "Back Camera" is the one a call wants.
 */
export function frontAndBack(cameras: readonly DeviceChoice[]): FrontAndBack | null {
  const front = cameras.find((c) => facingOf(c.label) === "front");
  const backs = cameras.filter((c) => facingOf(c.label) === "back");
  if (!front || backs.length === 0) return null;
  const back =
    backs.find((c) => /^back camera$/i.test(c.label.trim())) ??
    backs.find((c) => !/ultra|tele|dual|triple|wide|macro|depth/i.test(c.label)) ??
    backs[0];
  return { front, back };
}

/** A camera's name in a list: Front and Back for the pair, its own name for any other lens. */
export function cameraName(choice: DeviceChoice, pair: FrontAndBack | null): string {
  if (pair?.front.id === choice.id) return "Front";
  if (pair?.back.id === choice.id) return "Back";
  return choice.label;
}

/** The cameras as a picker lists them: a phone's pair named Front and Back, every other camera by its own name. */
export function namedCameras(cameras: readonly DeviceChoice[]): DeviceChoice[] {
  const pair = frontAndBack(cameras);
  return cameras.map((c) => ({ ...c, label: cameraName(c, pair) }));
}
